import React, { useCallback, useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import api, { formatApiErrorDetail } from '../api';
import { formatPts } from '../lib/format';
import Toast from '../components/Toast';

/**
 * AdminDemo — control panel for the "Launch Simulation" demo pod.
 * Everything here is real: real users, real bonding curve, real royalties.
 * Just isolated from production data via `demo_pod: "launch_sim"` marker.
 */
export default function AdminDemo() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [copied, setCopied] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/demo/state');
      setState(data);
    } catch (ex) {
      setToast({ msg: formatApiErrorDetail(ex.response?.data?.detail) || 'Could not load demo state', kind: 'error' });
    }
  }, []);

  useEffect(() => {
    load();
    // Poll every 2s while a run is active so the panel is live
    const iv = setInterval(() => {
      load();
    }, 2000);
    return () => clearInterval(iv);
  }, [load]);

  const reset = async () => {
    if (busy) return;
    if (!window.confirm('Reset the demo? This wipes all trades, chat and posts on the demo clan and refunds every bot to their starting balance.')) return;
    setBusy(true);
    try {
      await api.post('/admin/demo/reset');
      setToast({ msg: '✓ Demo pod reset — ready for next run.' });
      await load();
    } catch (ex) {
      setToast({ msg: formatApiErrorDetail(ex.response?.data?.detail) || 'Reset failed', kind: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const seed = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post('/admin/demo/seed');
      setToast({ msg: `✓ Seeded — ${data.bots} bots ready` });
      await load();
    } catch (ex) {
      setToast({ msg: formatApiErrorDetail(ex.response?.data?.detail) || 'Seed failed', kind: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const copy = async (label, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
      setTimeout(() => setCopied(''), 1500);
    } catch { /* clipboard blocked */ }
  };

  const phase = state?.status?.phase;
  const running = state?.status?.running;
  const fillPct = state ? Math.min(100, Math.round((state.bits_minted / Math.max(1, state.supply_cap)) * 100)) : 0;

  return (
    <AdminLayout active="demo">
      <div className="admin-page" data-testid="admin-demo-page">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <h1 className="admin-page-title">🎬 Demo Environment</h1>
            <p className="admin-page-subtitle">
              Scripted launch simulation for creator pitches. Everything runs on real tokenomics — the numbers you see are exactly what a real creator would earn.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn-secondary" onClick={seed} disabled={busy} data-testid="demo-seed-btn">
              {state?.seeded ? 'Re-seed missing bots' : 'Seed demo pod'}
            </button>
            <button className="btn-primary" onClick={reset} disabled={busy || !state?.creator_exists} data-testid="demo-reset-btn">
              {busy ? '…' : 'Reset demo pod'}
            </button>
          </div>
        </div>

        {!state ? (
          <div className="admin-card" style={{ marginTop: 24 }}>Loading…</div>
        ) : (
          <>
            {/* Live status banner */}
            <div className="admin-card" style={{ marginTop: 20, borderLeft: running ? '3px solid #4ade80' : '3px solid #6b7280' }} data-testid="demo-status-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                <div>
                  <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1.2, opacity: 0.6 }}>Simulation status</div>
                  <div style={{ fontSize: 22, fontWeight: 800, marginTop: 4 }}>
                    {running ? (
                      <>
                        <span style={{ color: '#4ade80' }}>● Live</span>
                        <span style={{ marginLeft: 12, textTransform: 'capitalize', opacity: 0.9 }}>{phase || 'starting'} phase</span>
                      </>
                    ) : phase === 'complete' ? (
                      <span style={{ color: '#00B4FF' }}>✓ Run complete</span>
                    ) : (
                      <span style={{ opacity: 0.7 }}>Idle</span>
                    )}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1.2, opacity: 0.6 }}>Supply filled</div>
                  <div style={{ fontSize: 26, fontWeight: 800 }}>{state.bits_minted} / {state.supply_cap}</div>
                  <div style={{ height: 6, background: 'rgba(255,255,255,0.08)', borderRadius: 3, marginTop: 6, width: 220 }}>
                    <div style={{ height: '100%', width: `${fillPct}%`, background: state.sold_out ? '#4ade80' : 'linear-gradient(90deg, #00B4FF, #B584F5)', borderRadius: 3, transition: 'width 0.4s' }} />
                  </div>
                </div>
              </div>
            </div>

            {/* Earnings grid — REAL numbers */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginTop: 16 }}>
              <div className="admin-card" data-testid="demo-metric-royalties">
                <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1.2, opacity: 0.6 }}>Royalties earned</div>
                <div style={{ fontSize: 26, fontWeight: 800, marginTop: 4 }}>{state.creator_royalties_earned.toFixed(2)} pts</div>
                <div style={{ fontSize: 12, opacity: 0.6 }}>5% of every buy + sell</div>
              </div>
              <div className="admin-card" data-testid="demo-metric-lv">
                <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1.2, opacity: 0.6 }}>Creator&apos;s Bit payout</div>
                <div style={{ fontSize: 26, fontWeight: 800, marginTop: 4 }}>{state.creator_lv_payout.toFixed(2)} pts</div>
                <div style={{ fontSize: 12, opacity: 0.6 }}>Cashed at sell-out</div>
              </div>
              <div className="admin-card" data-testid="demo-metric-total">
                <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1.2, opacity: 0.6 }}>Total creator earned</div>
                <div style={{ fontSize: 28, fontWeight: 800, marginTop: 4, color: '#4ade80' }}>{state.creator_total_earned.toFixed(2)} pts</div>
                <div style={{ fontSize: 12, opacity: 0.6 }}>≈ ${state.creator_total_earned.toFixed(0)} USD equivalent</div>
              </div>
              <div className="admin-card" data-testid="demo-metric-balance">
                <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1.2, opacity: 0.6 }}>Live wallet balance</div>
                <div style={{ fontSize: 26, fontWeight: 800, marginTop: 4 }}>{formatPts(state.creator_balance, 2)} pts</div>
                <div style={{ fontSize: 12, opacity: 0.6 }}>Started at 1,000 pts</div>
              </div>
            </div>

            {/* Login credentials */}
            <div className="admin-card" style={{ marginTop: 16 }}>
              <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1.2, opacity: 0.6, marginBottom: 10 }}>Demo creator login (share with creators)</div>
              <div style={{ display: 'grid', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <code style={{ background: 'rgba(0,0,0,0.3)', padding: '6px 10px', borderRadius: 6, fontSize: 14 }}>{state.creator_email}</code>
                  <button className="btn-ghost" style={{ padding: '4px 10px' }} onClick={() => copy('email', state.creator_email)} data-testid="demo-copy-email-btn">
                    {copied === 'email' ? '✓ Copied' : '📋 Copy'}
                  </button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <code style={{ background: 'rgba(0,0,0,0.3)', padding: '6px 10px', borderRadius: 6, fontSize: 14 }}>{state.creator_password}</code>
                  <button className="btn-ghost" style={{ padding: '4px 10px' }} onClick={() => copy('password', state.creator_password)} data-testid="demo-copy-password-btn">
                    {copied === 'password' ? '✓ Copied' : '📋 Copy'}
                  </button>
                </div>
                <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
                  <strong>New flow (Feb 2026):</strong> After each reset, the creator's clan is fully wiped. Login → the app routes to the <strong>Create Clan wizard</strong>. Mint the clan → then tap <strong>🚀 Start Launch Simulation</strong> at the top of Manage Clan whenever you're ready to pitch. Bots stay quiet until you click.
                </div>
              </div>
            </div>

            {/* Pod info */}
            <div className="admin-card" style={{ marginTop: 16 }}>
              <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1.2, opacity: 0.6, marginBottom: 10 }}>Pod details (after mint)</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, fontSize: 14 }}>
                <div><span style={{ opacity: 0.6 }}>Clan handle</span><br /><strong>@{state.clan_handle}</strong></div>
                <div><span style={{ opacity: 0.6 }}>Suggested vibe</span><br /><strong>Focused</strong> (5 Bits to join)</div>
                <div><span style={{ opacity: 0.6 }}>Suggested audience</span><br /><strong>1K – 10K</strong></div>
                <div><span style={{ opacity: 0.6 }}>Suggested lifetime value</span><br /><strong>500 pts</strong></div>
                <div><span style={{ opacity: 0.6 }}>Bot accounts</span><br /><strong>{state.bot_count}</strong></div>
                <div><span style={{ opacity: 0.6 }}>Sold out</span><br /><strong>{state.sold_out ? 'Yes ✓' : 'No'}</strong></div>
              </div>
            </div>

            <div className="admin-card" style={{ marginTop: 16, background: 'rgba(0,180,255,0.05)', borderColor: 'rgba(0,180,255,0.2)' }}>
              <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                <strong>How the run works (~5 min after you tap Launch):</strong>
                <ul style={{ margin: '8px 0 0 20px', padding: 0, opacity: 0.9 }}>
                  <li><strong>Mint</strong> (0s) — Demo creator finishes the Create Clan wizard.</li>
                  <li><strong>Manual start</strong> — Creator taps <strong>🚀 Start Launch Simulation</strong> at the top of Manage Clan.</li>
                  <li><strong>Rush</strong> (0–60s) — 25 bots buy small amounts, price starts climbing.</li>
                  <li><strong>Momentum</strong> (60–120s) — 20 more bots join + chat + first bot post.</li>
                  <li><strong>Fill</strong> (120–210s) — hard push until SOLD OUT — Creator&apos;s Bit auto-cashes for the LV.</li>
                  <li><strong>🎁 Perks</strong> (210–260s) — 8–12 bots redeem seeded perks (1:1 calls, shoutouts, signed merch) → second revenue stream flows in.</li>
                  <li><strong>Churn</strong> (260–290s) — 25% of holders sell 30–60% of their stack — price dips visibly.</li>
                  <li><strong>Cooldown</strong> (290–300s) — a few dip-buys to close on a positive note.</li>
                </ul>
              </div>
            </div>
          </>
        )}
      </div>

      {toast && <Toast msg={toast.msg} onDone={() => setToast(null)} />}
    </AdminLayout>
  );
}
